import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Pencil, Globe, Trophy, Zap, Key, Copy, RefreshCw, LogIn, UserPlus } from "lucide-react";

const AVATARS = ["🌐", "🤖", "🔥", "🧠", "⚡", "🛡️", "🎯", "💼", "🦊", "🐺", "🦁", "🐍", "🎭", "🚀", "💎"];

interface ExternalBot {
  id: string;
  name: string;
  description: string;
  avatar: string;
  ownerId: string;
  callbackUrl: string;
  apiKey: string;
  modeSupport: string[];
  rating: number;
  matchesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  active: boolean;
  createdAt?: string;
  club?: string;
}

// Inline auth form — no separate page, keeps hooks stable
function InlineAuth() {
  const { login, register } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (tab === "login") {
        await login(email, password);
        toast({ title: "Вход выполнен" });
      } else {
        await register(email, password);
        toast({ title: "Регистрация успешна" });
      }
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-md mx-auto p-6 mt-8">
      <div className="text-center mb-4">
        <h2 className="text-lg font-semibold">Авторизация</h2>
        <p className="text-xs text-muted-foreground mt-1">Войдите для управления внешними ботами</p>
      </div>
      <div className="flex gap-2 mb-4">
        <Button variant={tab === "login" ? "default" : "outline"} size="sm" className="flex-1 gap-1" onClick={() => setTab("login")}>
          <LogIn className="w-3 h-3" /> Вход
        </Button>
        <Button variant={tab === "register" ? "default" : "outline"} size="sm" className="flex-1 gap-1" onClick={() => setTab("register")}>
          <UserPlus className="w-3 h-3" /> Регистрация
        </Button>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs mb-1 block">Email</Label>
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" type="email" data-testid="input-auth-email" />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Пароль</Label>
          <Input value={password} onChange={e => setPassword(e.target.value)} placeholder={tab === "register" ? "Минимум 6 символов" : "Пароль"}
            type="password" data-testid="input-auth-password" />
        </div>
        <Button className="w-full" disabled={loading || !email || (tab === "register" ? password.length < 6 : !password)}
          onClick={handleSubmit} data-testid="button-auth-submit">
          {loading ? "Подождите..." : tab === "login" ? "Войти" : "Зарегистрироваться"}
        </Button>
      </div>
    </Card>
  );
}

export default function ExternalBotsPage() {
  const { isAuthenticated, email, logout } = useAuth();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editBot, setEditBot] = useState<ExternalBot | null>(null);
  const [showKeyFor, setShowKeyFor] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [avatar, setAvatar] = useState("🌐");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [club, setClub] = useState("");
  const [supportExpress, setSupportExpress] = useState(true);
  const [supportClassic, setSupportClassic] = useState(false);

  // Edit form
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAvatar, setEditAvatar] = useState("🌐");
  const [editUrl, setEditUrl] = useState("");
  const [editClub, setEditClub] = useState("");
  const [editExpress, setEditExpress] = useState(true);
  const [editClassic, setEditClassic] = useState(false);

  // ALL hooks called before any conditional return
  const { data: bots = [], isLoading } = useQuery<ExternalBot[]>({
    queryKey: ["/api/external-bots"],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: isAuthenticated,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const modes = [];
      if (supportExpress) modes.push("express");
      if (supportClassic) modes.push("classic");
      const res = await apiRequest("POST", "/api/external-bots", { name, description, avatar, callbackUrl, club, modeSupport: modes });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-bots"] });
      setCreateOpen(false);
      setName(""); setDescription(""); setAvatar("🌐"); setCallbackUrl(""); setClub(""); setSupportExpress(true); setSupportClassic(false);
      toast({ title: "Бот зарегистрирован" });
    },
    onError: (e: any) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editBot) return;
      const modes = [];
      if (editExpress) modes.push("express");
      if (editClassic) modes.push("classic");
      const res = await apiRequest("PUT", `/api/external-bots/${editBot.id}`, {
        name: editName, description: editDescription, avatar: editAvatar, callbackUrl: editUrl, club: editClub, modeSupport: modes,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-bots"] });
      setEditBot(null);
      toast({ title: "Бот обновлён" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/external-bots/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-bots"] });
      toast({ title: "Бот удалён" });
    },
  });

  const regenKeyMutation = useMutation({
    mutationFn: async (botId: string) => {
      const res = await apiRequest("POST", `/api/external-bots/${botId}/regenerate-key`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-bots"] });
      toast({ title: "API-ключ обновлён" });
    },
  });

  const openEdit = (bot: ExternalBot) => {
    setEditBot(bot);
    setEditName(bot.name);
    setEditDescription(bot.description);
    setEditAvatar(bot.avatar);
    setEditUrl(bot.callbackUrl);
    setEditClub(typeof bot.club === "string" ? bot.club : "");
    setEditExpress(bot.modeSupport.includes("express"));
    setEditClassic(bot.modeSupport.includes("classic"));
  };

  const copyKey = (key: string) => {
    navigator.clipboard?.writeText(key);
    toast({ title: "API-ключ скопирован" });
  };

  // Not authenticated — show auth inline
  if (!isAuthenticated) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold">Внешние боты</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Зарегистрируйте внешних ботов с webhook-подключением для участия в поединках на арене и чемпионатах
          </p>
        </div>
        <InlineAuth />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold" data-testid="text-ext-bots-title">Мои внешние боты</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Зарегистрируйте ботов с webhook endpoint для участия в поединках и чемпионатах
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{email}</span>
          <Button variant="outline" size="sm" onClick={logout} data-testid="button-logout">Выйти</Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="button-create-ext-bot">
                <Plus className="w-4 h-4" /> Добавить бота
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Регистрация внешнего бота</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Аватар</Label>
                    <Select value={avatar} onValueChange={setAvatar}>
                      <SelectTrigger className="w-16 text-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>{AVATARS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs mb-1 block">Имя бота</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Мой HR-бот" data-testid="input-ext-bot-name" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Описание</Label>
                  <Textarea value={description} onChange={e => setDescription(e.target.value)}
                    placeholder="Бот для переговоров по найму персонала. Использует стратегию win-win..."
                    rows={3} data-testid="input-ext-bot-desc" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Callback URL (webhook endpoint)</Label>
                  <Input value={callbackUrl} onChange={e => setCallbackUrl(e.target.value)}
                    placeholder="https://my-bot.example.com/bot/turn" data-testid="input-ext-bot-url" />
                  <p className="text-xs text-muted-foreground mt-1">Платформа будет отправлять POST-запросы на этот URL для каждого хода бота</p>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Клуб <span className="text-muted-foreground font-normal">(необязательно)</span></Label>
                  <Input value={club} onChange={e => setClub(e.target.value)} placeholder="Название клуба" data-testid="input-ext-bot-club" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Поддерживаемые режимы</Label>
                  <div className="flex items-center gap-4 mt-1">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={supportExpress} onCheckedChange={(v) => setSupportExpress(!!v)} />
                      Экспресс
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={supportClassic} onCheckedChange={(v) => setSupportClassic(!!v)} />
                      Классика
                    </label>
                  </div>
                </div>
                <Button className="w-full" disabled={!name || !callbackUrl || createMutation.isPending}
                  onClick={() => createMutation.mutate()} data-testid="button-save-ext-bot">
                  {createMutation.isPending ? "Регистрирую..." : "Зарегистрировать бота"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editBot} onOpenChange={(o) => { if (!o) setEditBot(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Редактирование бота</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-3">
              <div>
                <Label className="text-xs mb-1 block">Аватар</Label>
                <Select value={editAvatar} onValueChange={setEditAvatar}>
                  <SelectTrigger className="w-16 text-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{AVATARS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs mb-1 block">Имя бота</Label>
                <Input value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Описание</Label>
              <Textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} rows={3} />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Callback URL</Label>
              <Input value={editUrl} onChange={e => setEditUrl(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Клуб <span className="text-muted-foreground font-normal">(необязательно)</span></Label>
              <Input value={editClub} onChange={e => setEditClub(e.target.value)} placeholder="Название клуба" data-testid="input-ext-bot-edit-club" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Режимы</Label>
              <div className="flex items-center gap-4 mt-1">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={editExpress} onCheckedChange={(v) => setEditExpress(!!v)} />
                  Экспресс
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={editClassic} onCheckedChange={(v) => setEditClassic(!!v)} />
                  Классика
                </label>
              </div>
            </div>
            {/* API Key section */}
            {editBot?.apiKey && (
              <div>
                <Label className="text-xs mb-1 block flex items-center gap-1"><Key className="w-3 h-3" /> API-ключ</Label>
                <div className="flex items-center gap-2">
                  <Input value={showKeyFor === editBot.id ? editBot.apiKey : "••••••••••••••••••••"} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => setShowKeyFor(showKeyFor === editBot.id ? null : editBot.id)}>
                    {showKeyFor === editBot.id ? "Скрыть" : "Показать"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyKey(editBot.apiKey)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => regenKeyMutation.mutate(editBot.id)} disabled={regenKeyMutation.isPending}>
                    <RefreshCw className={`w-3 h-3 ${regenKeyMutation.isPending ? "animate-spin" : ""}`} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Используйте ключ для авторизации webhook-запросов от бота</p>
              </div>
            )}
            <Button className="w-full" disabled={!editName || !editUrl || updateMutation.isPending}
              onClick={() => updateMutation.mutate()} data-testid="button-update-ext-bot">
              {updateMutation.isPending ? "Сохраняю..." : "Сохранить"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Card key={i} className="p-5 animate-pulse"><div className="h-4 bg-muted rounded w-1/2 mb-3" /><div className="h-3 bg-muted rounded w-3/4" /></Card>)}
        </div>
      ) : bots.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Globe className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">У вас пока нет зарегистрированных ботов</p>
          <p className="text-xs mt-1">Добавьте бота с webhook endpoint для участия в поединках</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bots.map(bot => (
            <Card key={bot.id} className="p-5 space-y-3 group cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => openEdit(bot)} data-testid={`card-ext-bot-${bot.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{bot.avatar || "🌐"}</span>
                  <div>
                    <h3 className="font-semibold text-sm">{bot.name}</h3>
                    <p className="text-xs text-muted-foreground truncate max-w-[180px]">{bot.callbackUrl}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                    onClick={(e) => { e.stopPropagation(); openEdit(bot); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(bot.id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {bot.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{bot.description}</p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {typeof bot.club === "string" && bot.club.trim() !== "" && (
                  <Badge variant="secondary" className="text-xs max-w-[160px] truncate" title={bot.club.trim()}>
                    Клуб · {bot.club.trim()}
                  </Badge>
                )}
                {bot.modeSupport.map(m => (
                  <Badge key={m} variant="outline" className="text-xs gap-1"><Zap className="w-3 h-3" /> {m}</Badge>
                ))}
                {bot.active ? (
                  <Badge className="text-xs bg-green-500/10 text-green-400 border-green-500/20">Активен</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Неактивен</Badge>
                )}
                {bot.apiKey && (
                  <Badge variant="outline" className="text-xs gap-1"><Key className="w-3 h-3" /> API</Badge>
                )}
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground pt-1 border-t border-border/50">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-primary" />{bot.rating} ELO</span>
                  <span>{bot.matchesPlayed} матчей</span>
                  <span className="text-green-400">{bot.wins}W</span>
                  <span className="text-red-400">{bot.losses}L</span>
                  <span>{bot.draws}D</span>
                </div>
                {bot.createdAt && (
                  <span className="text-[10px] opacity-80">
                    Создан: {new Date(bot.createdAt).toLocaleString("ru-RU")}
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
